import { useRef } from "react";
import useTruncatedElement from "~/hooks/useTruncatedElement";
import { PLAY_GENRES } from "~/services/const";

type Props = {
  synopsis: string | null;
  ageRating: number | null;
  duration: number | null;
  releaseDate: string | null;
  genre: string | null;
  company: string | null;
  director: string | null;
  playwriter: string | null;
  tech_sheet: string | null;
};

export default function PlayDetails({
  synopsis,
  ageRating,
  duration,
  releaseDate,
  genre,
  company,
  director,
  playwriter,
  tech_sheet,
  media,
}: Props) {
  const ref = useRef(null);
  const { isTruncated, isReadingMore, setIsReadingMore } = useTruncatedElement({
    ref,
  });

  return (
    <div className="flex flex-col gap-8 p-4 sm:p-8 ">
      <div className="flex flex-wrap flex-row gap-2 sm:gap-4 mb-0">
        {genre && <Tag>{PLAY_GENRES.find((g) => g.id === genre)?.lang_pt}</Tag>}
        {ageRating && <Tag>Maiores de {ageRating}</Tag>}
        {duration && <Tag>{duration} minutos</Tag>}
      </div>
      {/* Synopsis Section */}
      {synopsis && (
        <div className="prose">
          <h2 className="text-lg font-medium mb-4 text-gray-400">Sinopse</h2>

          <div ref={ref} className={`text-gray-600 break-words ${!isReadingMore && "line-clamp-5"}`}>
            {synopsis}
          </div>
          {isTruncated && !isReadingMore && (
            <button className="text-sm underline text-slate-500" onClick={() => setIsReadingMore(true)}>
              Ver mais
            </button>
          )}
          {isTruncated && isReadingMore && (
            <button className="text-sm underline text-slate-500" onClick={() => setIsReadingMore(false)}>
              Ver menos
            </button>
          )}
        </div>
      )}

      <div className="">
        <h2 className="text-lg font-medium mb-4 text-gray-400">Ficha Técnica</h2>
        <dl className="grid grid-cols-3">
          {!!releaseDate && (
            <>
              <DT>Data de Estreia</DT>
              <DD>{new Date(releaseDate).toLocaleDateString("pt-PT")}</DD>
            </>
          )}

          {director && (
            <>
              <DT>Encenação</DT>
              <DD>{director}</DD>
            </>
          )}
          {playwriter && (
            <>
              <DT>Texto</DT>
              <DD>{playwriter}</DD>
            </>
          )}
          {company && (
            <>
              <DT>Companhia</DT>
              <DD>{company}</DD>
            </>
          )}
        </dl>
      </div>

      {/* Technical Sheet Section */}
      {tech_sheet && (
        <div className="prose">
          <h2 className="text-2xl font-medium mb-4">Informação Técnica</h2>
          <div className="text-gray-600 whitespace-pre-line">{tech_sheet}</div>
        </div>
      )}
    </div>
  );
}

function Tag({ children }) {
  return <div className="text-slate-500 px-2 sm:px-4 py-2 text-sm rounded bg-slate-100 font-medium">{children}</div>;
}

function DT({ children }) {
  return <dt className="text-sm sm:text-base text-gray-900 py-2 pr-4 border-b border-gray-100">{children}</dt>;
}

function DD({ children }) {
  return <dd className="text-sm sm:text-base text-gray-600 col-span-2 py-2 border-b border-gray-100">{children}</dd>;
}
